/**
 * Error Reporting Provider
 *
 * React context provider that initializes error reporting on the client.
 * Wrap your app with this provider to enable:
 * - Automatic breadcrumb capture (navigation, clicks, console, HTTP)
 * - Global error handlers (uncaught errors, unhandled rejections)
 *
 * @example
 * ```tsx
 * // In your root layout.tsx
 * import { ErrorReportingProvider } from '@/lib/error-reporting/client';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <ErrorReportingProvider>
 *           {children}
 *         </ErrorReportingProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */

'use client';

import { useEffect, useRef, useMemo, createContext, useContext, type ReactNode } from 'react';
import { initAutoBreadcrumbs } from './breadcrumbs';
import { installGlobalErrorHandlers } from './reporter';
import { isErrorReportingEnabled } from '../config';

// =============================================================================
// CONTEXT
// =============================================================================

interface ErrorReportingContextValue {
  /** Whether error reporting is enabled */
  isEnabled: boolean;
}

const ErrorReportingContext = createContext<ErrorReportingContextValue>({
  isEnabled: false,
});

/**
 * Hook to access error reporting context.
 * Mostly useful for checking if error reporting is enabled.
 */
export function useErrorReportingContext(): ErrorReportingContextValue {
  // eslint-disable-next-line @eslint-react/no-use-context -- deferred to React 19 use() migration
  return useContext(ErrorReportingContext);
}

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface ErrorReportingProviderProps {
  children: ReactNode;
  /**
   * Whether to capture breadcrumbs automatically.
   * Includes: navigation, clicks, console, HTTP.
   * @default true
   */
  autoBreadcrumbs?: boolean;
  /**
   * Whether to install global error handlers.
   * Captures: uncaught errors, unhandled promise rejections.
   * @default true
   */
  globalHandlers?: boolean;
}

/**
 * Provider component that initializes error reporting.
 *
 * Features enabled by default:
 * - Auto breadcrumbs: navigation, clicks, console, fetch
 * - Global handlers: window.onerror, unhandledrejection
 *
 * All features respect the ERROR_REPORTING_ENABLED env var.
 */
export function ErrorReportingProvider({
  children,
  autoBreadcrumbs = true,
  globalHandlers = true,
}: ErrorReportingProviderProps) {
  // Use ref to prevent double-initialization in strict mode
  const initializedRef = useRef(false);

  useEffect(() => {
    // Check if enabled
    if (!isErrorReportingEnabled()) {
      return;
    }

    // Prevent double-init in React strict mode
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const cleanups: (() => void)[] = [];

    // Initialize auto breadcrumbs
    if (autoBreadcrumbs) {
      cleanups.push(initAutoBreadcrumbs());
    }

    // Install global handlers
    if (globalHandlers) {
      cleanups.push(installGlobalErrorHandlers());
    }

    // Cleanup on unmount
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      initializedRef.current = false;
    };
  }, [autoBreadcrumbs, globalHandlers]);

  // Memoize context value to prevent cascading re-renders
  const contextValue = useMemo<ErrorReportingContextValue>(
    () => ({
      isEnabled: isErrorReportingEnabled(),
    }),
    []
  );

  return <ErrorReportingContext value={contextValue}>{children}</ErrorReportingContext>;
}

export default ErrorReportingProvider;
